import { useEffect } from 'react'
import cn from 'classnames'
import { useFlowData } from '@forwardflow/features/campaign-flow/store/flow-data.store'
import {
   IncomingCall,
   LiveCall,
   MissedCall,
} from '@forwardflow/features/campaign-flow/components/phone/CallScreens'
import {
   IPhone,
   LockScreen,
   MessagesScreen,
   VoicemailScreen,
} from '@forwardflow/features/campaign-flow/components/phone/TestPhone'
import { FlowIcon } from '@forwardflow/features/campaign-flow/components/ui/FlowIcon'
import { SmallButton } from '@forwardflow/features/campaign-flow/components/ui/primitives'
import { useTestRun } from '@forwardflow/features/campaign-flow/hooks/useTestRun'
import {
   useSpeech,
   useSpeechFill,
   useVoiceOption,
} from '@forwardflow/features/campaign-flow/hooks/useFlowHelpers'
import { useVoiceCall } from '@forwardflow/features/campaign-flow/hooks/useVoiceCall'
import { viewFor } from '@forwardflow/features/campaign-flow/utils/playbook'
import {
   callPrompt,
   standInVoiceFor,
} from '@forwardflow/features/campaign-flow/utils/voice-call'
import {
   activeType,
   useCampaignFlowStore,
} from '@forwardflow/features/campaign-flow/store/flow.store'

type TRun = ReturnType<typeof useTestRun>

const HINT: Record<TRun['phase'], string> = {
   idle: 'Press Start. The first call rings and you answer it.',
   ringing: 'Accept to talk to the caller, or decline to hear the voicemail.',
   live: 'Answer in your own words or tap a reply. Hang up any time.',
   ended: 'Move on to the next step when you are ready.',
   missed: 'This is what the lead finds when they miss the call.',
   text: 'Reply as the lead to see how it answers.',
}

/**
 * "Try it as a lead": the test phone in a drawer that slides in from the right
 * and stays open while the user edits any part of the playbook. The user plays
 * the lead: accepts or declines calls, talks back, replies to texts.
 */
export const LeadPhoneDrawer = ({
   open,
   onClose,
   run,
}: {
   open: boolean
   onClose: () => void
   run: TRun
}) => {
   const voiceName = useCampaignFlowStore((s) => s.draft.voice.name) || 'Erin'
   const voice = useVoiceOption()
   const { speak, speakingKey } = useSpeech()
   const item = run.item
   const SAMPLE_LEAD = useFlowData((st) => st.lead)
   const ORG = useFlowData((st) => st.org)
   /* A real voice call for the call touches: the agent gets this playbook's words. */
   const call = useVoiceCall()
   const say = useSpeechFill()
   const playbook = useCampaignFlowStore(
      (s) => s.draft.playbook,
      (a, b) => a === b
   )
   const prompt = () =>
      callPrompt(viewFor(playbook, editTypeNow()), say, {
         leadFirstName: SAMPLE_LEAD.firstName,
         city: SAMPLE_LEAD.city,
         source: leadSourceNow(),
         assistantName: voiceName,
         agentName: ORG.agentName,
      })
   const voiceId = standInVoiceFor(voice.gender, voice.defaultName)
   const accept = () => {
      run.accept()
      void call.start({ prompt: prompt(), voiceId })
   }
   const hangUp = () => {
      void call.end()
      run.hangUp()
   }
   /* Leaving the call screen ends the voice call. */
   const onCall = !!item && item.kind === 'call' && run.phase === 'live'
   useEffect(() => {
      if (!onCall && (call.status === 'live' || call.status === 'starting'))
         void call.end()
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [onCall, open])
   const spoken = call.status === 'live' || call.status === 'starting'
   /* The agent's signed URL is fetched when the phone opens, so Accept connects at once. */
   useEffect(() => {
      if (open) void call.prefetch().catch(() => undefined)
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [open])
   /* The sample lead looks like the people this campaign calls. */
   const audience = useCampaignFlowStore((s) => s.draft.audience)
   /* …and the lead type whose words are being edited. */
   const editType = useCampaignFlowStore(activeType)
   const leadSource =
      audience.filters
         .find((f) => f.field === 'source' && f.operator !== 'NOT_CONTAINS_ANY')
         ?.value.map(String)[0] ?? SAMPLE_LEAD.source
   const leadSourceNow = () => leadSource
   const editTypeNow = () => editType
   const leadKind = { buyer: 'buyer', seller: 'seller', rental: 'renter' }[
      editType
   ]
   const when = item ? `Day ${item.day}` : ''

   const screen = !item ? (
      <LockScreen
         day="Day 1"
         time="9:41"
         leadName={SAMPLE_LEAD.firstName}
         leadNote={`${leadSource} ${leadKind} · ${SAMPLE_LEAD.city}`}
         hint="Press Start to get the first call"
      />
   ) : item.kind === 'call' ? (
      run.phase === 'ringing' ? (
         <IncomingCall
            name={voiceName}
            company=""
            hue={voice.hue}
            onAccept={accept}
            onDecline={run.decline}
         />
      ) : run.phase === 'missed' ? (
         run.voicemail ? (
            <VoicemailScreen
               from={voiceName}
               meta={`${when} · missed call`}
               transcript={run.voicemail}
               playing={speakingKey === item.key}
               onPlay={() => speak(item.key, run.voicemail ?? '')}
            />
         ) : (
            <MissedCall name={voiceName} company="" when={when} />
         )
      ) : (
         <LiveCall
            name={voiceName}
            company=""
            hue={voice.hue}
            lines={spoken ? call.lines : run.lines}
            ended={run.phase === 'ended'}
            thinking={spoken ? call.speaking : run.thinking}
            quickReplies={run.quickReplies}
            onSay={run.say}
            onHangUp={hangUp}
            mic={
               call.status === 'starting'
                  ? 'connecting'
                  : call.status === 'live'
                    ? 'live'
                    : undefined
            }
            note={
               call.status === 'unavailable' && call.error
                  ? `${call.error} Typed rehearsal instead.`
                  : undefined
            }
         />
      )
   ) : (
      <MessagesScreen
         company=""
         number={ORG.phoneNumber}
         when={when}
         thread={run.thread}
         highlight={item.index}
         onReply={run.reply}
      />
   )

   return (
      <>
         <div
            className={cn(
               'fixed inset-0 z-40 bg-black/30 transition-opacity lg:hidden',
               open ? 'opacity-100' : 'opacity-0 pointer-events-none'
            )}
            onClick={onClose}
         />
         <aside
            className={cn(
               'fixed top-0 right-0 z-50 h-full w-[400px] max-w-full bg-[#1d1e23] border-l border-white/10 shadow-[-24px_0_60px_rgba(0,0,0,.45)] flex flex-col transition-transform duration-300',
               open ? 'translate-x-0' : 'translate-x-full'
            )}
            aria-hidden={!open}
            data-testid="cf-phone-drawer"
            data-open={open}
         >
            <div className="flex items-center gap-2 px-5 h-16 border-b border-white/[0.06] shrink-0">
               <span className="w-8 h-8 rounded-full bg-[#00d857]/15 text-[#00d857] flex items-center justify-center">
                  <FlowIcon name="phone" size={14} />
               </span>
               <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">Try it as a lead</div>
                  <div className="text-xs text-[#9ca3af] truncate">
                     {item
                        ? `${item.label} · ${when}`
                        : `You are ${SAMPLE_LEAD.firstName}`}
                  </div>
               </div>
               <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white/60 hover:bg-white/10 hover:text-white"
                  data-testid="cf-phone-close"
               >
                  <FlowIcon name="x" size={16} />
               </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto cf-scroll flex flex-col items-center gap-3 py-5">
               <IPhone testId="cf-phone">{screen}</IPhone>
               <div className="flex gap-1.5">
                  <SmallButton
                     icon="refresh"
                     onClick={run.reset}
                     testId="cf-run-reset"
                  >
                     Restart
                  </SmallButton>
                  {item ? (
                     <SmallButton
                        icon="chevs"
                        variant="primary"
                        onClick={run.next}
                        testId="cf-run-next"
                     >
                        {run.nextItem
                           ? `Next: ${run.nextItem.label}`
                           : 'Finish'}
                     </SmallButton>
                  ) : (
                     <SmallButton
                        icon="play"
                        variant="primary"
                        onClick={run.start}
                        testId="cf-run-start"
                     >
                        Start
                     </SmallButton>
                  )}
               </div>
               <p className="text-xs text-[#818181] text-center max-w-[250px]">
                  {HINT[run.phase]}
               </p>
            </div>
         </aside>
      </>
   )
}
